import { Controller, useFormContext } from "react-hook-form";
import { DynDadosCadastraisProps } from ".";
import { Grid, Stack, TextField, Typography } from "@mui/material";
import SelectConvenio from "../../../SelectsSimpleApiQuery/SelectConvenio";
import axiosClient from "../../../../lib/axios";
import moment from "moment";
import SelectBancos from "../../../SelectsSimpleApiQuery/SelectBancos";
import { permitirApenasNumeros } from "../../../../helpers/masks";
import { useEffect, useMemo } from "react";
import { desformatarCPFCNPJ } from "../../../../helpers/unmask";
import BasicDatePicker from "../../../BasicDatePicker";

export default function DadosCooperado({ spec, extras }: DynDadosCadastraisProps) {
    const { control, getValues, setValue } = useFormContext();

    const isPessoaJuridica = useMemo(() => extras.cpf_cnpj !== undefined && extras.cpf_cnpj !== "" && extras.cpf_cnpj.length === 14, [extras])

    useEffect(() => {
        const current = getValues(`${spec.name}`);
        if (current === undefined || current === null) {
            setValue(`${spec.name}.aliquota_inss`, 1.2);
            setValue(`${spec.name}.aliquota_senar`, 0.30);
        }
    }, [getValues, setValue, spec.name, isPessoaJuridica]);
    return (
        <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'}>Dados complementares</Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
                <Controller
                    name={`${spec.name}.data_adimissao`}
                    control={control}
                    rules={{
                        required: 'Campo obrigatório',
                        validate: (value) => {
                            const dataMoment = moment(value, "YYYY-MM-DD", true);
                            if (!dataMoment.isValid()) return "Data inválida";
                            return true;
                        }
                    }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <BasicDatePicker
                            required
                            label="Data de Admissão"
                            helperText={error ? error.message : ""}
                            error={invalid}
                            value={field.value ? moment(field.value, "YYYY-MM-DD") : null}
                            onChange={(data) => {
                                field.onChange(data ? moment(data).format("YYYY-MM-DD") : null);
                            }}
                            formatString="DD/MM/YYYY"
                            readonly={false}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 4 }}>
                <Controller
                    name={`${spec.name}.convenio`}
                    control={control}
                    render={({ field }) => (
                        <SelectConvenio
                            {...field}
                            value={field.value}
                            onChange={(value) => {
                                field.onChange(value)
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 4 }}>
                <Controller
                    name={`${spec.name}.inscricao_estadual`}
                    control={control}
                    rules={{
                        validate: async (value) => {

                            if (!value || value.trim() === '') {
                                return true;
                            }

                            const uf = getValues(`${spec.name}.uf`);
                            if (!uf || uf === "") {
                                return "Selecione o UF antes de preencher a inscrição estadual";
                            }

                            try {
                                const response = await axiosClient.post(`/cadastros/verificar-inscricao-estadual`, {
                                    inscricao_estadual: desformatarCPFCNPJ(value),
                                    uf: uf,
                                });

                                if (response.status === 200) {
                                    return true;
                                }
                                return response.data?.mensagem || 'Inscrição estadual inválida!';
                            } catch (error: any) {
                                console.error('Erro ao validar inscrição estadual:', error);
                                if (error.response?.data?.mensagem) {
                                    return error.response.data.mensagem;
                                }
                                return 'Erro ao validar inscrição estadual. Verifique os dados informados.';
                            }
                        }
                    }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            label='Inscrição Estadual'
                            type="text"
                            fullWidth
                            value={field.value}
                            onChange={(e) => field.onChange((e.target.value))}
                            error={invalid}
                            helperText={error ? error.message : ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 6, md: 4 }}>
                <Controller
                    name={`${spec.name}.banco`}
                    control={control}
                    render={({ field }) => (
                        <SelectBancos
                            {...field}
                            value={field.value}
                            onChange={(value) => {
                                field.onChange(value)
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Controller
                        name={`${spec.name}.conta`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                label='Conta'
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange(permitirApenasNumeros(e.target.value))}
                            />
                        )}
                    />
                    -
                    <Controller
                        name={`${spec.name}.conta_digito`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                label='Digito'
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange((e.target.value))}
                            />
                        )}
                    />
                </Stack>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Controller
                        name={`${spec.name}.agencia`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                label='Agência'
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange(permitirApenasNumeros(e.target.value))}
                            />
                        )}
                    />
                    -
                    <Controller
                        name={`${spec.name}.agencia_digito`}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                label='Agência digito'
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange((e.target.value))}
                            />
                        )}
                    />
                </Stack>
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.aliquota_inss`}
                    control={control}
                    rules={{ validate: (value) => parseFloat(value) <= 100 || "A alíquota deve ser menor ou igual a 100%" }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            defaultValue={isPessoaJuridica ? 2.75 : 2.30}
                            label='Aliquota INSS'
                            type="number"
                            fullWidth
                            value={field.value}
                            onChange={(e) => field.onChange((e.target.value))}
                            slotProps={{ htmlInput: { max: 100 } }}
                            error={invalid}
                            helperText={error ? error.message : ""}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.aliquota_senar`}
                    control={control}
                    rules={{ validate: (value) => parseFloat(value) <= 100 || "A alíquota deve ser menor ou igual a 100%" }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            label='Aliquota SENAR'
                            defaultValue={0.20}
                            type="number"
                            fullWidth
                            value={field.value}
                            onChange={(e) => field.onChange((e.target.value))}
                            slotProps={{ htmlInput: { max: 100 } }}
                            error={invalid}
                            helperText={error ? error.message : ""}
                        />
                    )}
                />
            </Grid>
        </Grid>
    )
}
